import React from "react"
import {
  DeliveryGiftCardProduct,
  Order,
  OrderRefund,
  Reason
} from "lib/types/generated/graphql-types"
import { Name, RefundButtonWrapper, Row } from "../Shared.styled"
import SecondaryButton from "../../../../Ui/Buttons/SecondaryButton"
import { useMutation } from "@apollo/client"
import CREATE_REFUND from "../../../../../graphql/mutations/order/refunds/CreateRefund"
import Money from "../../../../Money"
import DineroFactory from "dinero.js"
import UPDATE_REFUND from "../../../../../graphql/mutations/order/refunds/UpdateRefund"
import { ReactComponent as RefundIcon } from "../../../../../images/icons/arrow-rotate-left-thick.svg"
import Status from "../../../../Ui/Status"
import { handleErrorMessages } from "helpers/errors"
import { Refund } from "../../../../../classes/order/Refund"
import { ReactComponent as GiftCardIcon } from "images/icons/gift-card.svg"
import { getRefundInput } from "../Helpers/helpers"

type Props = {
  giftCardProduct: DeliveryGiftCardProduct
  order: Order
  currencyCode: string
  refunds: OrderRefund[]
  refetch: () => void
  reason: Reason
}

export const GiftCardProductRow = ({
  giftCardProduct,
  order,
  currencyCode,
  refetch,
  reason
}: Props) => {
  const refunds = new Refund(order.Refunds)

  const giftCardProductRefunded = refunds
    .completedRefunds()
    .find((refund) =>
      refund?.giftCardProducts?.find(
        (gcp) => gcp.giftCardProductId == giftCardProduct?.giftCardProductId
      )
    )

  const [createRefund, { loading: createLoading }] = useMutation(CREATE_REFUND, {
    onCompleted: () => {
      refetch()
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const [updateRefund, { loading: updateLoading }] = useMutation(UPDATE_REFUND, {
    onCompleted: () => {
      refetch()
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const orderLineExistInRefund = (id: string) => {
    return refunds.getOnGoingRefund()?.giftCardProducts.find((gcp) => gcp.giftCardProductId === id)
  }

  const createOrUpdateOrderRefund = () => {
    if (!refunds.onGoing()) {
      return createRefund({
        variables: {
          ...(reason?.cause && reason?.code && { reason }),
          orderId: order.id,
          input: {
            giftCardProducts: [{ giftCardProductId: giftCardProduct.giftCardProductId }]
          }
        }
      })
    }
    return updateRefund({
      variables: {
        refundId: refunds.getOnGoingRefund()?.id,
        input: {
          ...getRefundInput(refunds.getOnGoingRefund()),
          giftCardProducts: [
            ...refunds.getOnGoingRefund()?.giftCardProducts?.map((gcp) => ({
              gcp
            })),
            { giftCardProductId: giftCardProduct.giftCardProductId }
          ]
        }
      }
    })
  }

  return (
    <Row key={giftCardProduct.giftCardProductId}>
      <GiftCardIcon />
      <div>
        <Name>Gift card</Name>
        <Money
          amount={giftCardProduct.priceAmount}
          currencyUnit={currencyCode as DineroFactory.Currency}
        ></Money>
      </div>
      <RefundButtonWrapper>
        {giftCardProductRefunded ? (
          <Status status="Refunded" />
        ) : (
          <SecondaryButton
            handleClick={createOrUpdateOrderRefund}
            disabled={!!orderLineExistInRefund(giftCardProduct.giftCardProductId)}
            loading={createLoading || updateLoading}
          >
            <RefundIcon />
            Refund
          </SecondaryButton>
        )}
        <span>
          {" "}
          Total :{" "}
          <b>
            {" "}
            <Money
              amount={giftCardProduct.priceAmount}
              currencyUnit={currencyCode as DineroFactory.Currency}
            />
          </b>
        </span>
      </RefundButtonWrapper>
    </Row>
  )
}
